export function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Two metallic tones only: gold and silver, over the night-blue surface.
export function avatarStyle(name = "") {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  const useGold = sum % 2 === 0;
  return useGold
    ? { background: "rgba(201,162,76,0.16)", color: "#D8B45E" }
    : { background: "rgba(199,208,222,0.14)", color: "#C7D0DE" };
}

export function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60) return "hace un momento";
  const min = Math.round(diff / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d === 1) return "ayer";
  if (d < 7) return `hace ${d} días`;
  const w = Math.round(d / 7);
  if (w < 5) return `hace ${w} sem`;
  return then.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

export function fullDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

export function todayLabel() {
  const s = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const BADGE = "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold";
const TONE = {
  neutral: "border-brand-line text-brand-muted bg-brand-panel2",
  gold: "border-brand-gold/40 text-brand-goldSoft bg-brand-gold/[0.14]",
  silver: "border-brand-silver/30 text-brand-silver bg-brand-silver/[0.10]",
  green: "border-brand-pos/40 text-brand-pos bg-brand-pos/[0.14]",
  red: "border-brand-neg/40 text-brand-neg bg-brand-neg/[0.14]",
};

export const STATUS_META = {
  descubierto: { label: "Descubierto", cls: `${BADGE} ${TONE.neutral}` },
  analizado: { label: "Analizado", cls: `${BADGE} ${TONE.neutral}` },
  seleccionado: { label: "Seleccionado", cls: `${BADGE} ${TONE.gold}` },
  por_contactar: { label: "Por contactar", cls: `${BADGE} ${TONE.gold}` },
  contactado: { label: "Contactado", cls: `${BADGE} ${TONE.gold}` },
  esperando: { label: "Esperando respuesta", cls: `${BADGE} ${TONE.silver}` },
  cualificado: { label: "Cualificado", cls: `${BADGE} ${TONE.gold}` },
  conversacion: { label: "En conversación", cls: `${BADGE} ${TONE.gold}` },
  oportunidad: { label: "Oportunidad", cls: `${BADGE} ${TONE.silver}` },
  reunion: { label: "Reunión", cls: `${BADGE} ${TONE.silver}` },
  cliente: { label: "Cliente", cls: `${BADGE} ${TONE.green}` },
  satisfaccion: { label: "Experiencia / satisfacción", cls: `${BADGE} ${TONE.green}` },
  promotor: { label: "Promotor / recomendador", cls: `${BADGE} ${TONE.green}` },
  referido: { label: "Referido generado", cls: `${BADGE} ${TONE.green}` },
  seguimiento: { label: "Seguimiento futuro", cls: `${BADGE} ${TONE.green}` },
  conseguido: { label: "Objetivo conseguido", cls: `${BADGE} ${TONE.green}` },
  en_pausa: { label: "En pausa", cls: `${BADGE} ${TONE.silver}` },
  no_interesado: { label: "No interesado", cls: `${BADGE} ${TONE.red}` },
  no_contactar: { label: "No contactar", cls: `${BADGE} ${TONE.red}` },
};

export const STATUS_OPTIONS = [
  "descubierto", "analizado", "seleccionado", "por_contactar", "contactado", "esperando",
  "cualificado", "conversacion", "oportunidad", "reunion", "cliente", "satisfaccion",
  "promotor", "referido", "seguimiento", "conseguido", "en_pausa", "no_interesado", "no_contactar",
];

// v7: estados que ve el usuario en la ficha (por dentro se mantienen los de siempre).
export const SIMPLE_STATUS = [
  ["por_contactar", "Por contactar"], ["contactado", "Contactado"], ["conversacion", "En conversación"],
  ["reunion", "Reunión"], ["cliente", "Cliente / socio"], ["conseguido", "Objetivo conseguido"],
  ["en_pausa", "En pausa"], ["no_interesado", "No interesado"], ["no_contactar", "No contactar"],
];
const SIMPLE_OF = {
  descubierto: "por_contactar", analizado: "por_contactar", seleccionado: "por_contactar", cualificado: "por_contactar",
  por_contactar: "por_contactar", contactado: "contactado", esperando: "contactado", conversacion: "conversacion",
  oportunidad: "conversacion", reunion: "reunion", cliente: "cliente", seguimiento: "cliente", satisfaccion: "cliente",
  promotor: "cliente", referido: "cliente", conseguido: "conseguido", en_pausa: "en_pausa",
  no_interesado: "no_interesado", no_contactar: "no_contactar",
};
export const simpleStatus = (s) => SIMPLE_OF[s] || "por_contactar";
export const simpleStatusLabel = (s) => (SIMPLE_STATUS.find(([k]) => k === simpleStatus(s)) || [])[1] || "Por contactar";

export const CHANNEL_META = {
  instagram: { label: "Instagram" },
  facebook: { label: "Facebook" },
  linkedin: { label: "LinkedIn" },
};

export const TYPE_META = {
  cliente: { label: "Cliente" },
  socio: { label: "Socio / distribuidor" },
  afiliado: { label: "Afiliado / colaborador" },
  seguidor: { label: "Seguidor cualificado" },
  recomendador: { label: "Recomendador" },
};

// v4: el backend a veces devuelve el objetivo en inglés (client, partner…); siempre se muestra en español.
const TYPE_ALIAS = { client: "cliente", partner: "socio", distributor: "socio", team_member: "socio", affiliate: "afiliado",
  collaborator: "afiliado", colaborador: "afiliado", recommender: "recomendador", qualified_follower: "seguidor" };
export const typeLabel = (k) => TYPE_META[TYPE_ALIAS[k] || k]?.label || "";

export const ACTION_META = {
  mensaje: { label: "Mensaje directo" },
  comentario: { label: "Comentario" },
  audio: { label: "Audio" },
  llamada: { label: "Llamada" },
  contenido: { label: "Contenido" },
};

export const STRATEGY_LIBRARY = [
  { id: "recurso_gratuito", label: "Ofrecer un recurso gratuito" },
  { id: "auditoria", label: "Revisión o auditoría gratuita" },
  { id: "conversacion", label: "Abrir conversación o llamada" },
  { id: "presentacion", label: "Presentar tu negocio" },
  { id: "colaboracion", label: "Proponer una colaboración" },
  { id: "directo_conjunto", label: "Directo conjunto" },
  { id: "intercambio", label: "Intercambio de audiencia" },
  { id: "afiliacion", label: "Proponer afiliación" },
  { id: "aporte_valor", label: "Aportar valor y nutrir" },
  { id: "recomendacion", label: "Pedir una recomendación" },
];

export function strategyLabel(id) {
  return (STRATEGY_LIBRARY.find((s) => s.id === id) || {}).label || "";
}

export const RESULT_OPTIONS = [
  { v: "enviado", label: "Enviado o realizado", status: "contactado", follow: "Esperar respuesta unos días" },
  { v: "respondio", label: "Respondió", status: "conversacion", follow: "Continuar la conversación y aportar valor" },
  { v: "interes", label: "Mostró interés", status: "oportunidad", follow: "Proponer el siguiente paso concreto" },
  { v: "pidio_info", label: "Pidió más información", status: "conversacion", follow: "Enviar la información pedida" },
  { v: "agendo", label: "Aceptó llamada o reunión", status: "reunion", follow: "Preparar la reunión y sus puntos" },
  { v: "no_respondio", label: "No respondió aún", status: "esperando", follow: "Valorar un seguimiento con otro ángulo" },
  { v: "no_interesado", label: "No le interesa por ahora", status: "no_interesado", follow: "Dejar reposar y retomar en 3 meses" },
  { v: "no_contactar", label: "No contactar", status: "no_contactar", follow: "" },
];

export function statusBadge(status) {
  return STATUS_META[status] || STATUS_META.descubierto;
}


// Etapas del CRM para la vista por columnas (P-05). Agrupan los estados internos sin modificarlos;
// al mover una tarjeta a una etapa, el contacto pasa al PRIMER estado de esa etapa.
export const CRM_STAGES = [
  { key: "por_contactar", label: "Por contactar", statuses: ["por_contactar", "descubierto", "analizado", "seleccionado", "cualificado"] },
  { key: "contactado", label: "Contactado", statuses: ["contactado", "esperando"] },
  { key: "conversacion", label: "En conversación", statuses: ["conversacion", "oportunidad"] },
  { key: "reunion", label: "Reunión", statuses: ["reunion"] },
  { key: "cliente", label: "Cliente", statuses: ["cliente", "seguimiento", "satisfaccion", "promotor", "referido", "conseguido"] },
  { key: "cerrado", label: "En pausa o cerrado", statuses: ["en_pausa", "no_interesado", "no_contactar"] },
];
export const stageOf = (status) => CRM_STAGES.find((s) => s.statuses.includes(status)) || CRM_STAGES[0];

// v26b · Etiqueta del lead en «personas por interés» (motor indirecto). El motivo nunca cita el comentario ni la cuenta.
export const LEAD_LABEL = {
  muy_interesado: { emoji: "🔥", label: "Muy interesado", order: 0, tone: "border-brand-pos/40 bg-brand-pos/[0.12] text-brand-pos" },
  buen_encaje: { emoji: "⭐", label: "Buen encaje", order: 1, tone: "border-brand-accent/40 bg-brand-accent/[0.10] text-brand-accent" },
  por_conocer: { emoji: "👋", label: "Por conocer", order: 2, tone: "border-brand-silver/30 bg-brand-silver/[0.10] text-brand-silver" },
};
export const leadLabelOrder = (k) => (LEAD_LABEL[k] ? LEAD_LABEL[k].order : 3);
